import React, { useEffect, useMemo, useState } from "react";
import api, { STAGE_LABELS, WORK_STAGES, STAGE_GIVEN_OPTIONS, STAGE_GIVEN_DEFAULT, formatHrLabel } from "@/lib/api";
import {
  Search, X, Copy, History, Flame, AlertTriangle, ChevronRight, Inbox,
  Calendar, User, Users, Tag, Layers, ClipboardList, FileText, Save, Trash2, MessageSquareText,
} from "lucide-react";
import { toast } from "sonner";

const BOARD_STAGES = ["assigned", "in_field", "drafter", "qa1", "qa2", "final_qc", "sent_back_to_field", "for_revision", "sent_to_client"];
const STAGE_BG = {
  assigned: "bg-zinc-200",
  in_field: "bg-amber-100",
  drafter: "bg-blue-100",
  qa1: "bg-yellow-100",
  qa2: "bg-orange-100",
  final_qc: "bg-indigo-100",
  sent_back_to_field: "bg-rose-100",
  for_revision: "bg-pink-100",
  sent_to_client: "bg-green-100",
};
const PRIORITY_META = {
  urgent: { bg: "bg-red-200", ring: "ring-red-500", icon: Flame, label: "URGENT" },
  high:   { bg: "bg-orange-200", ring: "ring-orange-500", icon: AlertTriangle, label: "HIGH" },
  normal: { bg: "bg-zinc-100", ring: "ring-zinc-400", icon: null, label: "NORMAL" },
  low:    { bg: "bg-zinc-50", ring: "ring-zinc-300", icon: null, label: "LOW" },
};

/**
 * Outlook-style, 3-pane Jobs board:
 *   [ Categories ] [ Job List (search + sort) ] [ Job Details (inline edit) ]
 *
 * The outer app sidebar is the 4th "pane" (rendered by the shell layout).
 * On mobile, only one pane is visible at a time — a bread-crumb-style
 * back button navigates categories -> list -> details.
 */
export default function OutlookJobsView({
  board,
  filteredBoard,
  users,
  clients,
  teams,
  isAdmin,
  staleMap,
  isOverdue,
  labelForJobClient,
  onChanged,           // reload the whole board
  onDuplicate,
  onAudit,
}) {
  const [categoryDim, setCategoryDim] = useState("status");       // status | priority
  const [selectedCategory, setSelectedCategory] = useState("all"); // "all" or stage / priority key
  const [jobSearch, setJobSearch] = useState("");
  const [jobSortBy, setJobSortBy] = useState("priority");         // priority | due | newest
  const [selectedId, setSelectedId] = useState(null);
  const [mobilePane, setMobilePane] = useState("categories");      // categories | list | details

  // Flatten filtered board once so the list pane can search / sort globally
  const flatJobs = useMemo(() => {
    const out = [];
    Object.entries(filteredBoard || {}).forEach(([stage, arr]) => {
      (arr || []).forEach((j) => out.push({ ...j, _stage: stage }));
    });
    return out;
  }, [filteredBoard]);

  // Total-count buckets for the categories pane — computed against ALL jobs (unfiltered),
  // so counts stay stable when the user tweaks the top-of-page filters.
  const categoryBuckets = useMemo(() => {
    const list = [];
    if (categoryDim === "status") {
      const counts = {};
      Object.entries(board || {}).forEach(([s, arr]) => { counts[s] = (arr || []).length; });
      BOARD_STAGES.forEach((s) => {
        list.push({ key: s, label: STAGE_LABELS[s] || s, color: STAGE_BG[s] || "bg-zinc-100", count: counts[s] || 0 });
      });
    } else {
      const counts = { urgent: 0, high: 0, normal: 0, low: 0 };
      Object.values(board || {}).flat().forEach((j) => {
        const p = (j && j.priority) || "normal";
        if (counts[p] !== undefined) counts[p]++;
      });
      ["urgent", "high", "normal", "low"].forEach((p) => {
        list.push({ key: p, label: PRIORITY_META[p].label, color: PRIORITY_META[p].bg, count: counts[p] });
      });
    }
    return list;
  }, [categoryDim, board]);

  // Reset selected category when dim switches
  useEffect(() => { setSelectedCategory("all"); }, [categoryDim]);

  // Filter + sort the middle pane
  const paneJobs = useMemo(() => {
    let arr = flatJobs;
    if (selectedCategory !== "all") {
      if (categoryDim === "status") arr = arr.filter((j) => j.stage === selectedCategory);
      else arr = arr.filter((j) => (j.priority || "normal") === selectedCategory);
    }
    const q = jobSearch.trim().toLowerCase();
    if (q) {
      arr = arr.filter((j) =>
        (j.job_number || "").toLowerCase().includes(q) ||
        (j.project_name || "").toLowerCase().includes(q) ||
        (j.client || "").toLowerCase().includes(q) ||
        (j.notes || "").toLowerCase().includes(q) ||
        Object.values(j.stage_assignees || {}).some((v) => (v || "").toLowerCase().includes(q))
      );
    }
    if (jobSortBy === "priority") {
      const order = { urgent: 0, high: 1, normal: 2, low: 3 };
      arr = [...arr].sort((a, b) => (order[a.priority || "normal"] - order[b.priority || "normal"]));
    } else if (jobSortBy === "due") {
      arr = [...arr].sort((a, b) => {
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return a.due_date.localeCompare(b.due_date);
      });
    } else {
      arr = [...arr].sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    }
    return arr;
  }, [flatJobs, selectedCategory, categoryDim, jobSearch, jobSortBy]);

  // Auto-select first job when the list changes and nothing is selected
  useEffect(() => {
    if (paneJobs.length === 0) { setSelectedId(null); return; }
    if (!paneJobs.find((j) => j.id === selectedId)) setSelectedId(paneJobs[0].id);
  }, [paneJobs, selectedId]);

  const selectedJob = useMemo(() => flatJobs.find((j) => j.id === selectedId) || null, [flatJobs, selectedId]);

  const onPickCategory = (key) => {
    setSelectedCategory(key);
    setMobilePane("list");
  };
  const onPickJob = (job) => {
    setSelectedId(job.id);
    setMobilePane("details");
  };

  // ---------- Layout ----------
  //  md+: 3-column grid | Categories 220-260px | List 340-420px | Details fills
  //  <md: single pane, breadcrumb navigation
  return (
    <div className="brut-card p-0 overflow-hidden" data-testid="outlook-jobs-view">
      {/* Mobile breadcrumb bar */}
      <div className="md:hidden flex items-center gap-2 border-b border-zinc-900 px-3 py-2 bg-yellow-50">
        <button
          className={`text-[11px] font-mono font-bold ${mobilePane === "categories" ? "underline" : "text-zinc-600"}`}
          onClick={() => setMobilePane("categories")}
        >CATEGORIES</button>
        <ChevronRight className="w-3 h-3 text-zinc-400" />
        <button
          className={`text-[11px] font-mono font-bold ${mobilePane === "list" ? "underline" : "text-zinc-600"}`}
          onClick={() => setMobilePane("list")}
        >JOBS ({paneJobs.length})</button>
        <ChevronRight className="w-3 h-3 text-zinc-400" />
        <button
          className={`text-[11px] font-mono font-bold ${mobilePane === "details" ? "underline" : "text-zinc-600"} ${!selectedJob ? "opacity-50" : ""}`}
          disabled={!selectedJob}
          onClick={() => selectedJob && setMobilePane("details")}
        >DETAILS</button>
      </div>

      <div
        className="md:flex md:flex-row"
        style={{ height: "calc(100vh - 260px)", minHeight: "560px", maxHeight: "calc(100vh - 200px)" }}
      >
        {/* ==== Pane 2 · Categories ==== */}
        <div
          className={`md:w-[220px] xl:w-[240px] shrink-0 border-r border-zinc-900 flex-col min-h-0 ${mobilePane === "categories" ? "flex" : "hidden md:flex"}`}
          data-testid="outlook-categories-pane"
        >
          <div className="p-2 border-b border-zinc-900 bg-zinc-50">
            <div className="flex items-center border border-zinc-900 bg-white rounded-full overflow-hidden text-[10px] font-mono font-bold">
              {["status", "priority"].map((d) => (
                <button
                  key={d}
                  onClick={() => setCategoryDim(d)}
                  className={`flex-1 px-2 py-1 ${categoryDim === d ? "bg-zinc-900 text-white" : "hover:bg-zinc-100"}`}
                  data-testid={`outlook-cat-dim-${d}`}
                >{d === "status" ? "BY STATUS" : "BY PRIORITY"}</button>
              ))}
            </div>
          </div>
          <div className="flex-1 overflow-y-auto py-1">
            <CategoryRow
              active={selectedCategory === "all"}
              label="ALL JOBS"
              color="bg-white"
              icon={<Inbox className="w-3.5 h-3.5" />}
              count={flatJobs.length}
              onClick={() => onPickCategory("all")}
              testId="outlook-cat-all"
            />
            <div className="border-t border-zinc-200 my-1" />
            {categoryBuckets.map((b) => (
              <CategoryRow
                key={b.key}
                active={selectedCategory === b.key}
                label={b.label}
                color={b.color}
                count={b.count}
                onClick={() => onPickCategory(b.key)}
                testId={`outlook-cat-${b.key}`}
              />
            ))}
          </div>
        </div>

        {/* ==== Pane 3 · Job List ==== */}
        <div
          className={`md:w-[360px] xl:w-[400px] shrink-0 border-r border-zinc-900 flex-col bg-white min-h-0 ${mobilePane === "list" ? "flex" : "hidden md:flex"}`}
          data-testid="outlook-list-pane"
        >
          <div className="p-2 border-b border-zinc-900 bg-zinc-50 space-y-2">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
              <input
                type="search"
                placeholder="Search jobs, clients, notes…"
                value={jobSearch}
                onChange={(e) => setJobSearch(e.target.value)}
                className="brut-input w-full pl-7 pr-7 py-1.5 text-xs"
                data-testid="outlook-search-input"
              />
              {jobSearch && (
                <button
                  onClick={() => setJobSearch("")}
                  className="absolute right-1 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-900"
                  data-testid="outlook-search-clear"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
            <div className="flex items-center justify-between gap-2">
              <div className="text-[10px] font-mono text-zinc-600" data-testid="outlook-list-count">
                {paneJobs.length} JOB{paneJobs.length === 1 ? "" : "S"}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[9px] font-mono text-zinc-500">SORT</span>
                <select
                  value={jobSortBy}
                  onChange={(e) => setJobSortBy(e.target.value)}
                  className="brut-input px-1.5 py-0.5 text-[10px] font-mono"
                  data-testid="outlook-sort-select"
                >
                  <option value="priority">Priority</option>
                  <option value="due">Due date</option>
                  <option value="newest">Newest</option>
                </select>
              </div>
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {paneJobs.length === 0 ? (
              <div className="text-center text-xs font-mono text-zinc-400 py-16 px-4">
                <Inbox className="w-8 h-8 mx-auto mb-2 opacity-40" />
                No jobs match this filter.
              </div>
            ) : (
              paneJobs.map((j) => (
                <ListRow
                  key={j.id}
                  job={j}
                  active={selectedId === j.id}
                  overdue={isOverdue(j)}
                  stale={!!staleMap?.[j.id]}
                  clientLabel={labelForJobClient(j)}
                  onClick={() => onPickJob(j)}
                />
              ))
            )}
          </div>
        </div>

        {/* ==== Pane 4 · Job Details ==== */}
        <div
          className={`md:flex-1 flex-col bg-white min-h-0 min-w-0 ${mobilePane === "details" ? "flex" : "hidden md:flex"}`}
          data-testid="outlook-details-pane"
        >
          {selectedJob ? (
            <JobDetailPane
              key={selectedJob.id}
              job={selectedJob}
              users={users}
              clients={clients}
              teams={teams}
              isAdmin={isAdmin}
              onChanged={onChanged}
              onDuplicate={() => onDuplicate(selectedJob)}
              onAudit={() => onAudit(selectedJob)}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-center text-sm font-mono text-zinc-400 p-6">
              <div>
                <ClipboardList className="w-10 h-10 mx-auto mb-2 opacity-40" />
                Select a job on the left to see its full details.
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function CategoryRow({ active, label, color, icon, count, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-3 py-2 text-left text-xs font-mono font-bold border-l-4 transition-colors ${
        active ? "bg-yellow-100 border-l-zinc-900" : "border-l-transparent hover:bg-zinc-50"
      }`}
      data-testid={testId}
    >
      {icon || <span className={`w-2.5 h-2.5 rounded-sm border border-zinc-900 ${color}`} />}
      <span className="flex-1 truncate uppercase">{label}</span>
      <span className={`text-[10px] px-1.5 py-0.5 border border-zinc-900 ${active ? "bg-zinc-900 text-white" : "bg-white"}`}>{count}</span>
    </button>
  );
}

// ---------------------------------------------------------------------------
function ListRow({ job, active, overdue, stale, clientLabel, onClick }) {
  const pri = PRIORITY_META[job.priority || "normal"];
  const Icon = pri.icon;
  const stageBg = STAGE_BG[job.stage] || "bg-zinc-100";
  const primaryAssignee = Object.values(job.stage_assignees || {}).find(Boolean) || "";
  return (
    <button
      onClick={onClick}
      data-testid={`outlook-row-${job.id}`}
      className={`w-full text-left px-3 py-2 border-b border-zinc-200 hover:bg-yellow-50 transition-colors block ${
        active ? "bg-yellow-100 border-l-4 border-l-zinc-900" : "border-l-4 border-l-transparent"
      } ${overdue ? "ring-inset ring-1 ring-red-300" : ""}`}
    >
      <div className="flex items-center justify-between gap-1 mb-0.5">
        <span className="font-mono text-[10px] text-zinc-500 truncate">{job.job_number}</span>
        <div className="flex items-center gap-1">
          {(job.priority === "urgent" || job.priority === "high") && Icon && (
            <span className={`flex items-center gap-0.5 text-[9px] font-mono font-bold px-1 py-0.5 border border-zinc-900 ${pri.bg}`}>
              <Icon className="w-2.5 h-2.5" />
            </span>
          )}
          <span className={`text-[9px] font-mono font-bold px-1 py-0.5 border border-zinc-900 ${stageBg} uppercase truncate max-w-[100px]`}>
            {STAGE_LABELS[job.stage] || job.stage}
          </span>
        </div>
      </div>
      <div className="font-bold text-sm leading-snug truncate">{job.project_name || "(no title)"}</div>
      <div className="flex items-center justify-between mt-1 text-[10px] font-mono text-zinc-600 gap-2">
        <span className="truncate">{clientLabel || "—"}</span>
        {job.due_date && (
          <span className={overdue ? "text-red-600 font-bold" : ""}>{overdue ? "OVERDUE · " : ""}{job.due_date}</span>
        )}
      </div>
      {primaryAssignee && (
        <div className="text-[10px] font-mono text-zinc-500 truncate mt-0.5">→ {primaryAssignee}</div>
      )}
      {stale && !overdue && (
        <div className="text-[9px] font-mono font-bold text-orange-700 mt-0.5">⏳ STUCK</div>
      )}
    </button>
  );
}

// ---------------------------------------------------------------------------
// The right pane — fully inline editor for a single job. Mirrors the fields on
// JobDetailModal but rendered flush inside a pane (no modal chrome).
function JobDetailPane({ job, users, clients, teams, isAdmin, onChanged, onDuplicate, onAudit }) {
  const [form, setForm] = useState(job);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(job), [job.id]);

  const assigneeOptions = useMemo(() => {
    if (form.team && Array.isArray(teams)) {
      const t = teams.find((x) => x.team === form.team);
      if (t) return t.members || [];
    }
    return users || [];
  }, [form.team, teams, users]);

  const onClientChange = (id) => {
    const c = (clients || []).find((x) => x.id === id);
    setForm({ ...form, client_id: id, client: c?.name || "" });
  };
  const setStageGiven = (s, v) => setForm((f) => ({ ...f, stage_time_given: { ...(f.stage_time_given || {}), [s]: v } }));
  const setStageTaken = (s, v) => setForm((f) => ({ ...f, stage_time_taken: { ...(f.stage_time_taken || {}), [s]: v === "" ? "" : parseFloat(v) || 0 } }));

  const stGiven = form.stage_time_given || {};
  const stTaken = form.stage_time_taken || {};
  const totalGiven = WORK_STAGES.reduce((sum, s) => sum + (parseFloat(stGiven[s]) || 0), 0);
  const totalTaken = WORK_STAGES.reduce((sum, s) => sum + (parseFloat(stTaken[s]) || 0), 0);
  const efficiency = totalTaken > 0 ? ((totalGiven / totalTaken) * 100).toFixed(2) : "—";

  const save = async () => {
    setSaving(true);
    try {
      const stTakenOut = {};
      const stGivenOut = {};
      for (const s of WORK_STAGES) {
        const v = stTaken[s];
        if (v !== "" && v !== null && v !== undefined && !isNaN(parseFloat(v))) stTakenOut[s] = parseFloat(v);
        const g = stGiven[s];
        if (g !== "" && g !== null && g !== undefined && !isNaN(parseFloat(g))) stGivenOut[s] = parseFloat(g);
      }
      const payload = {
        project_name: form.project_name, client: form.client, client_id: form.client_id,
        team: form.team, stage: form.stage, status: form.status, priority: form.priority,
        category: form.category,
        stage_time_given: stGivenOut,
        stage_time_taken: stTakenOut,
        stage_assignees: form.stage_assignees, notes: form.notes, due_date: form.due_date,
      };
      if (form.rate_override === "" || form.rate_override === null || form.rate_override === undefined) {
        payload.rate_override = null;
      } else {
        payload.rate_override = parseFloat(form.rate_override);
      }
      await api.patch(`/jobs/${job.id}`, payload);
      toast.success("Saved");
      onChanged();
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const del = async () => {
    if (!confirm("Delete this job?")) return;
    try { await api.delete(`/jobs/${job.id}`); toast.success("Deleted"); onChanged(); }
    catch { toast.error("Delete failed"); }
  };

  const pri = PRIORITY_META[form.priority || "normal"];
  const PriIcon = pri.icon;
  const stageBg = STAGE_BG[form.stage] || "bg-zinc-100";
  const clientLabel = isAdmin
    ? (form.client || "—")
    : ((clients || []).find((c) => c.id === form.client_id)?.initials || form.client || "—");

  return (
    <div className="flex-1 flex flex-col min-h-0" data-testid={`outlook-detail-${job.id}`}>
      {/* Sticky header */}
      <div className="border-b border-zinc-900 p-4 bg-white flex flex-wrap items-start justify-between gap-3 shrink-0">
        <div className="min-w-0 flex-1">
          <div className="label-mono text-zinc-500">{form.job_number}</div>
          <h2 className="font-heading text-xl sm:text-2xl font-black leading-tight truncate">{form.project_name || "(untitled)"}</h2>
          <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
            <span className={`text-[10px] font-mono font-bold px-2 py-0.5 border border-zinc-900 ${stageBg} uppercase`}>{STAGE_LABELS[form.stage] || form.stage}</span>
            {PriIcon && (form.priority === "urgent" || form.priority === "high") && (
              <span className={`flex items-center gap-0.5 text-[10px] font-mono font-bold px-2 py-0.5 border border-zinc-900 ${pri.bg}`}>
                <PriIcon className="w-3 h-3" /> {pri.label}
              </span>
            )}
            {form.category && form.category !== "live" && (
              <span className={`text-[10px] font-mono font-bold px-2 py-0.5 border border-zinc-900 ${form.category === "practice" ? "bg-blue-100" : "bg-purple-100"} uppercase`}>{form.category}</span>
            )}
            <span className="text-[10px] font-mono text-zinc-600 truncate">{clientLabel}</span>
            {form.team && <span className="text-[10px] font-mono px-1.5 py-0.5 bg-zinc-100 border border-zinc-300">{form.team}</span>}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5">
          <button onClick={onAudit} className="brut-btn-secondary px-2.5 py-1.5 text-xs flex items-center gap-1" data-testid="outlook-detail-audit">
            <History className="w-3 h-3" /> HISTORY
          </button>
          <button onClick={onDuplicate} className="brut-btn-secondary px-2.5 py-1.5 text-xs flex items-center gap-1" data-testid="outlook-detail-duplicate">
            <Copy className="w-3 h-3" /> DUPLICATE
          </button>
          <button onClick={save} disabled={saving} className="brut-btn px-3 py-1.5 text-xs flex items-center gap-1 disabled:opacity-60" data-testid="outlook-detail-save">
            <Save className="w-3 h-3" /> {saving ? "SAVING…" : "SAVE"}
          </button>
        </div>
      </div>

      {/* Scrollable body */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Quick-fact strip */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px] font-mono">
          <Fact icon={<Calendar className="w-3 h-3" />} label="DUE" value={form.due_date || "—"} />
          <Fact icon={<Layers className="w-3 h-3" />} label="STAGE" value={STAGE_LABELS[form.stage] || "—"} />
          <Fact icon={<Tag className="w-3 h-3" />} label="STATUS" value={(form.status || "—").replace(/_/g, " ")} />
          <Fact icon={<User className="w-3 h-3" />} label="EFFICIENCY" value={`${efficiency}${efficiency !== "—" ? "%" : ""}`} />
        </div>

        {/* Editable fields */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="PROJECT NAME">
            <input className="brut-input w-full px-3 py-2 text-sm" value={form.project_name || ""} onChange={(e) => setForm({ ...form, project_name: e.target.value })} data-testid="outlook-project-input" />
          </Field>
          <Field label="CLIENT">
            <select className="brut-input w-full px-3 py-2 text-sm" value={form.client_id || ""} onChange={(e) => onClientChange(e.target.value)} data-testid="outlook-client-select">
              <option value="">— select client —</option>
              {(clients || []).map((c) => <option key={c.id} value={c.id}>{isAdmin ? c.name : (c.initials || c.name)}</option>)}
            </select>
          </Field>
          <Field label="TEAM">
            <select className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.team || ""} onChange={(e) => setForm({ ...form, team: e.target.value })} data-testid="outlook-team-select">
              <option value="">— select team —</option>
              {(teams || []).map((t) => (
                <option key={t.team} value={t.team}>{t.team} ({t.member_count})</option>
              ))}
              {form.team && !(teams || []).some((t) => t.team === form.team) && (
                <option value={form.team}>{form.team}</option>
              )}
            </select>
          </Field>
          <Field label="CATEGORY">
            <select className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.category || "live"} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="outlook-category-select">
              <option value="live">Live (billable)</option>
              <option value="practice">Practice</option>
              <option value="training">Training</option>
            </select>
          </Field>
          <Field label="STAGE">
            <select className="brut-input w-full px-3 py-2 text-sm" value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })} data-testid="outlook-stage-select">
              {BOARD_STAGES.map((s) => <option key={s} value={s}>{STAGE_LABELS[s]}</option>)}
            </select>
          </Field>
          <Field label="STATUS">
            <select className="brut-input w-full px-3 py-2 text-sm" value={form.status || "in_progress"} onChange={(e) => setForm({ ...form, status: e.target.value })} data-testid="outlook-status-select">
              <option value="in_progress">In Progress</option>
              <option value="completed">Completed</option>
              <option value="sent_to_client">Sent to Client</option>
              <option value="hold">Hold</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </Field>
          <Field label="PRIORITY">
            <select className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.priority || "normal"} onChange={(e) => setForm({ ...form, priority: e.target.value })} data-testid="outlook-priority-select">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          </Field>
          <Field label="DUE DATE">
            <input type="date" className="brut-input w-full px-3 py-2 text-sm font-mono" value={form.due_date || ""} onChange={(e) => setForm({ ...form, due_date: e.target.value })} data-testid="outlook-due-input" />
          </Field>
        </div>

        {/* Per-stage time */}
        <div className="border border-zinc-900">
          <div className="bg-zinc-900 text-white px-3 py-1.5 label-mono">PER-STAGE TIME · GIVEN VS TAKEN</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-zinc-50 text-left">
                  <th className="px-2 py-1 label-mono">Stage</th>
                  <th className="px-2 py-1 label-mono">Given</th>
                  <th className="px-2 py-1 label-mono">Taken</th>
                  <th className="px-2 py-1 label-mono">Assignee</th>
                </tr>
              </thead>
              <tbody>
                {WORK_STAGES.map((s) => (
                  <tr key={s} className="border-t border-zinc-200">
                    <td className="px-2 py-1.5 font-bold text-xs whitespace-nowrap">{STAGE_LABELS[s]}</td>
                    <td className="px-2 py-1.5">
                      <select
                        className="brut-input px-1 py-0.5 text-xs font-mono w-24"
                        value={stGiven[s] ?? STAGE_GIVEN_DEFAULT[s]}
                        onChange={(e) => setStageGiven(s, parseFloat(e.target.value))}
                        data-testid={`outlook-stage-given-${s}`}
                        disabled={STAGE_GIVEN_OPTIONS[s].length === 1}
                      >
                        {STAGE_GIVEN_OPTIONS[s].map((h) => <option key={h} value={h}>{formatHrLabel(h)}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        type="number" step="0.05" min="0"
                        className="brut-input px-1 py-0.5 text-xs font-mono w-20"
                        value={stTaken[s] ?? ""}
                        onChange={(e) => setStageTaken(s, e.target.value)}
                        placeholder="0"
                        data-testid={`outlook-stage-taken-${s}`}
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <select
                        className="brut-input px-1 py-0.5 text-xs w-full min-w-[140px]"
                        value={form.stage_assignees?.[s] || ""}
                        onChange={(e) => setForm({ ...form, stage_assignees: { ...(form.stage_assignees || {}), [s]: e.target.value } })}
                        data-testid={`outlook-stage-assignee-${s}`}
                      >
                        <option value="">— assign{form.team ? ` from ${form.team}` : ""} —</option>
                        {assigneeOptions.map((u) => <option key={u.user_id} value={u.name}>{u.name}</option>)}
                      </select>
                    </td>
                  </tr>
                ))}
                <tr className="border-t-2 border-zinc-900 bg-yellow-50 font-bold">
                  <td className="px-2 py-1.5 label-mono">TOTAL</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{totalGiven.toFixed(2)} h</td>
                  <td className="px-2 py-1.5 font-mono text-xs">{totalTaken.toFixed(2)} h</td>
                  <td className="px-2 py-1.5 font-mono text-xs">EFF: {efficiency}{efficiency !== "—" ? "%" : ""}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <Field label="RATE OVERRIDE (BLANK = USE CLIENT DEFAULT)">
          <input type="number" step="0.01" className="brut-input w-full px-3 py-2 text-sm font-mono"
            value={form.rate_override ?? ""}
            onChange={(e) => setForm({ ...form, rate_override: e.target.value })}
            placeholder="Custom price for this specific job"
            data-testid="outlook-rate-input"
          />
        </Field>

        <Field label="NOTES">
          <textarea
            className="brut-input w-full px-3 py-2 text-sm" rows={4}
            value={form.notes || ""}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
            data-testid="outlook-notes-input"
          />
        </Field>

        {/* Full-field mirror: everything else present on the job object. This
            is the "show all created fields" that the user requested — anything
            not modelled as an editable input above still shows up read-only. */}
        <RawFieldMirror job={job} />

        {/* Danger zone */}
        <div className="border border-red-300 bg-red-50 p-3 flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs font-mono text-red-700">DANGER ZONE · this cannot be undone</div>
          <button onClick={del} className="text-xs font-bold text-red-600 hover:underline flex items-center gap-1" data-testid="outlook-delete-job">
            <Trash2 className="w-3 h-3" /> DELETE JOB
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="label-mono mb-1">{label}</div>
      {children}
    </label>
  );
}

function Fact({ icon, label, value }) {
  return (
    <div className="border border-zinc-900 bg-white p-2">
      <div className="flex items-center gap-1 text-[9px] font-mono text-zinc-500 uppercase">{icon}{label}</div>
      <div className="font-bold text-sm truncate">{value}</div>
    </div>
  );
}

// Small helper that surfaces raw job fields NOT already exposed as editable
// inputs. Deliberately read-only so we don't clobber structural columns.
const SUPPRESSED_KEYS = new Set([
  "id", "_id", "_stage",
  "job_number", "project_name", "client", "client_id", "client_initials",
  "team", "category", "stage", "status", "priority", "rate_override",
  "stage_assignees", "stage_time_given", "stage_time_taken",
  "notes", "due_date",
  "given_time_hours", "taken_time_hours", "efficiency",
]);
function RawFieldMirror({ job }) {
  const entries = Object.entries(job || {})
    .filter(([k, v]) => !SUPPRESSED_KEYS.has(k) && v !== null && v !== undefined && v !== "" && (typeof v !== "object" || (Array.isArray(v) && v.length > 0) || (typeof v === "object" && Object.keys(v).length > 0)));
  if (entries.length === 0) return null;

  const fmt = (v) => {
    if (typeof v === "string") return v;
    if (typeof v === "number" || typeof v === "boolean") return String(v);
    try { return JSON.stringify(v); } catch { return String(v); }
  };

  return (
    <details className="border border-zinc-300 bg-zinc-50" data-testid="outlook-raw-fields">
      <summary className="cursor-pointer px-3 py-1.5 label-mono flex items-center gap-1 select-none">
        <FileText className="w-3 h-3" /> ALL FIELDS ({entries.length})
      </summary>
      <div className="px-3 py-2 space-y-1 text-[11px] font-mono max-h-64 overflow-y-auto">
        {entries.map(([k, v]) => (
          <div key={k} className="flex items-start gap-2">
            <span className="text-zinc-500 uppercase w-40 shrink-0 truncate">{k}</span>
            <span className="flex-1 break-all whitespace-pre-wrap">{fmt(v)}</span>
          </div>
        ))}
      </div>
    </details>
  );
}
